import React, { useState, useEffect } from 'react';
import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from 'hooks/useMemorizeInputsFilters';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  CardHeader,
  Button,
  CardBody,
  Label
} from 'reactstrap';

import QueueService from '../../../Queue/service';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import FlexSelect from 'components/Inputs/FlexSelect';
import Loader from 'react-loader-spinner';
import HourMaskMMHHSS from '../../../../components/Inputs/HourMaskMMHHSS.js';
import DatePicker from '../../../../components/Inputs/DatePicker.js';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import ChatBotReportService from './service';
import Pagination from 'components/Pagination';
import FlexIAService from '../../../FlexIA/service';

import {
  dateRules,
  hourRules,
  queueNameId,
  toISOFormat,
  flexiaToId
} from './utils';
import TableAnalytical from './tables/Analytical';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { ClearFiltersButton } from 'components/ClearFiltersButton';
import getFlexIAType from 'views/FlexIA/utils/getFlexIAType';
import { ItemPerPageSelect } from 'components/Inputs/ItemPerPageSelect';

const errorFormMessage = (message) => <p style={{ color: 'red' }}>{message}</p>;

const formatDates = (date, time) => {
  const [day, month, year] = date.split('/');
  const [hour, minute, second] = time.split(':');
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
};

const ChatBotReport = () => {
  const { control, handleSubmit, errors, setValue, watch, getValues } =
    useForm();
  const { getMemorizedFilters, memorizeFilters, clearMemorizedFilters } =
    useMemorizeFilters(POSSIBLE_FILTERS_ENTITIES.REPORT_ADVANCED_CHATBOT);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [flexiaList, setFlexiaList] = useState([]);
  const [optionsWatson, setOptionsWatson] = useState([]);
  const [queueShowDisable, setQueueShowDisable] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [quantity, setQuantity] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const optionWatson = watch('flexia');

  async function loadTableConversation(pagina, override = null) {
    setLoadingReport(true);

    const params = override || {
      finalDate: formatDates(watch('dateBegin'), watch('timeStart')),
      initialDate: formatDates(watch('dateEnd'), watch('timeEnd')),
      flexiaId: optionWatson,
      options: getValues()['watsonOption'],
      queueId: getValues()['queue'],
      page: pagina,
      limit: itemsPerPage
    };

    const response = await ChatBotReportService.getHistoryWatson(params);

    if (response?.data?.data.length === 0) {
      toast.info('Selecione buscar para obter resultados!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }

    setDataTable(response?.data?.data);
    setQuantity(response?.data?.totalFiltered);
    setLoadingReport(false);
  }

  const handlePageChange = async (page) => {
    const prevFilters = getMemorizedFilters();
    memorizeFilters({ ...prevFilters, page });
    setActivePage(page);

    const loadTableParams = {
      finalDate: formatDates(prevFilters?.dateEnd, prevFilters?.timeEnd),
      initialDate: formatDates(prevFilters?.dateBegin, prevFilters?.timeStart),
      flexiaId: optionWatson,
      options: getValues()['watsonOption'],
      queueId: getValues()['queue'],
      page: page,
      limit: itemsPerPage
    };
    loadTableConversation(page, loadTableParams);
  };

  useEffect(() => {
    if (!optionWatson) return;
    ChatBotReportService.getOptionsWatson(optionWatson).then((response) => {
      const options = response?.data.data.map((option) => ({
        label: option,
        value: option
      }));
      setOptionsWatson(options ?? []);
    });
  }, [optionWatson]);

  useEffect(() => {
    QueueService.getAllShowDisable(queueShowDisable)
      .then((response) => {
        setQueues(response.data.data);
      })
      .catch((err) => {
        console.log('err: ' + err);
      });
  }, [queueShowDisable]);

  useEffect(() => {
    getFlexia();
  }, []);

  useEffect(() => {
    if (flexiaList.length > 0) {
      const formValues = getValues();
      onSubmit(formValues, false);
    }
  }, [flexiaList]);

  const getFlexia = () => {
    FlexIAService.getAllFlexia(1, 100, '', { showDisable: false })
      .then((response) => {
        setLoading(false);

        setFlexiaList(
          response.data.result
            .filter(
              (flexia) => flexia.type === 'WATSON' || flexia.type === 'DIALOG'
            )
            .map((flexia) => {
              return {
                ...flexia,
                optionSubtitle: getFlexIAType(flexia.type || '')
              };
            })
        );
      })
      .catch((err) => {
        toast.error(
          (err.response && err.response.data.message) ||
            'Erro ao listar configurações da FlexIA!',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
      });
  };

  const handleReset = () => {
    const defaultFieldValues = {
      watson: '',
      watsonOption: '',
      queue: '',
      dateBegin: new Date().toLocaleDateString('pt-br'),
      timeStart: '00:00:00',
      dateEnd: new Date().toLocaleDateString('pt-br'),
      timeEnd: '23:59:59'
    };

    for (const key in defaultFieldValues) {
      setValue(`${key}`, defaultFieldValues[key]);
    }

    clearMemorizedFilters();
  };

  const onSubmit = async (data, isNotFromUseEffect = true) => {
    try {
      let pageSavedOnMemory = 1;

      if (!isNotFromUseEffect) {
        pageSavedOnMemory = getMemorizedFilters()?.page ?? 1;
      }

      setActivePage(pageSavedOnMemory);
      memorizeFilters({ ...data, page: pageSavedOnMemory });

      setLoadingReport(true);
      const response = await ChatBotReportService.getHistoryWatson({
        finalDate: formatDates(data?.dateEnd, data?.timeEnd),
        initialDate: formatDates(data?.dateBegin, data?.timeStart),
        flexiaId: optionWatson,
        options: getValues()['watsonOption'],
        queueId: getValues()['queue'],
        page: pageSavedOnMemory,
        limit: itemsPerPage
      });

      if (response?.data?.data.length === 0) {
        toast.info(
          'Selecione os filtros e depois buscar para obter resultados!',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
      }

      setDataTable(response?.data?.data);
      setQuantity(response?.data?.totalFiltered);
      setLoadingReport(false);
    } catch (err) {
      console.log(err);
      setLoadingReport(false);
      toast.error(err || 'Ops, ocorreu um erro!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        showBackArrow="not"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3>Relatório de Chatbot</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <Loader
                        type="TailSpin"
                        color={systemConfiguration?.primary_color}
                        height={100}
                        width={100}
                      />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <ClearFiltersButton handleOnClick={handleReset} />
                      <Row className="mb-3">
                        <Col md="4">
                          <label className="form-control-label">FlexIA</label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={flexiaList}
                                isMulti={true}
                                isClearable={true}
                                value={props.value}
                                closeMenuOnSelect={true}
                                valueController={setValue}
                                fieldName="flexia"
                                labelName="name"
                                valueName="id"
                              />
                            )}
                            control={control}
                            name="flexia"
                            defaultValue={getMemorizedFilters()?.flexia ?? ''}
                          />
                        </Col>
                        <Col sm="4">
                          <label className="form-control-label">
                            Opção do Fluxo
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={optionsWatson}
                                isMulti={true}
                                value={props.value}
                                valueController={setValue}
                                closeMenuOnSelect={true}
                                isClearable={true}
                                fieldName="watsonOption"
                                labelName="label"
                                valueName="value"
                                isDisabled={
                                  optionWatson && optionWatson?.length > 0
                                    ? false
                                    : true
                                }
                              />
                            )}
                            control={control}
                            name="watsonOption"
                            defaultValue={
                              getMemorizedFilters()?.watsonOption ?? ''
                            }
                          />
                        </Col>
                        <Col md="4">
                          <label className="form-control-label">Fila</label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={queues}
                                isMulti={true}
                                isClearable={true}
                                value={props.value}
                                valueController={setValue}
                                closeMenuOnSelect={true}
                                fieldName="queue"
                                labelName="name"
                                valueName="id"
                              />
                            )}
                            control={control}
                            name="queue"
                            defaultValue={getMemorizedFilters()?.queue ?? ''}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Data inicial*
                          </label>
                          <Controller
                            render={(props) => (
                              <DatePicker
                                fieldName="dateBegin"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="dateBegin"
                            rules={dateRules}
                            defaultValue={
                              getMemorizedFilters()?.dateBegin ??
                              new Date().toLocaleDateString('pt-br')
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="dateBegin"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Hora inicial*
                          </label>
                          <Controller
                            render={(props) => (
                              <HourMaskMMHHSS
                                valueController={setValue}
                                fieldName="timeStart"
                                value={props.value}
                              />
                            )}
                            control={control}
                            name="timeStart"
                            rules={hourRules}
                            defaultValue={
                              getMemorizedFilters()?.timeStart ?? '00:00:00'
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="timeStart"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Data final*
                          </label>
                          <Controller
                            render={(props) => (
                              <DatePicker
                                fieldName="dateEnd"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="dateEnd"
                            defaultValue={
                              getMemorizedFilters()?.dateEnd ??
                              new Date().toLocaleDateString('pt-br')
                            }
                            rules={{
                              ...dateRules,
                              validate: (value) => {
                                if (
                                  new Date(toISOFormat(value, true)) <
                                  new Date(
                                    toISOFormat(getValues()['dateBegin'], true)
                                  )
                                ) {
                                  return 'A data final não pode ser anterior à inicial!';
                                } else {
                                  return true;
                                }
                              }
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="dateEnd"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Hora final*
                          </label>
                          <Controller
                            render={(props) => (
                              <HourMaskMMHHSS
                                valueController={setValue}
                                fieldName="timeEnd"
                                value={props.value}
                              />
                            )}
                            control={control}
                            name="timeEnd"
                            rules={hourRules}
                            defaultValue={
                              getMemorizedFilters()?.timeEnd ?? '23:59:59'
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="timeEnd"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3" style={{ alignItems: 'center' }}>
                        <Col md="12">
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Label style={{ marginBottom: 0 }}>
                              Mostrar desabilitados:
                            </Label>
                            <Label
                              style={{ marginBottom: 0, marginLeft: '5px' }}
                            >
                              Filas
                            </Label>
                            <ToggleSwitch
                              checked={queueShowDisable}
                              id="showDisableQueueReportChatBot"
                              onChange={() =>
                                setQueueShowDisable(!queueShowDisable)
                              }
                            />
                          </div>
                        </Col>
                      </Row>
                      <hr />
                      <Button
                        style={buttonStyle(systemConfiguration?.primary_color)}
                        type="submit"
                      >
                        Buscar
                      </Button>
                      <Col
                        md="3"
                        style={{ marginTop: '-45px', marginLeft: '80px' }}
                      >
                        <ItemPerPageSelect valueController={setItemsPerPage} />
                      </Col>
                    </Form>
                  )}

                  {loadingReport ? (
                    <Row className="justify-content-md-center">
                      <Loader
                        type="TailSpin"
                        color={systemConfiguration?.primary_color}
                        height={100}
                        width={100}
                      />
                    </Row>
                  ) : (
                    dataTable?.length > 0 && (
                      <>
                        <TableAnalytical
                          data={dataTable}
                          flexia={flexiaToId(flexiaList)}
                          queues={queueNameId(queues)}
                          totalRegister={quantity}
                        />
                        <Pagination
                          activePage={activePage}
                          totalItemsCount={quantity}
                          itemsCountPerPage={itemsPerPage}
                          onChange={handlePageChange.bind(this)}
                        />
                      </>
                    )
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default ChatBotReport;
